/**
 * Public (unauthenticated) org/trainer page — the AUTHENTICATED editing API
 * (Task 9). Every resource (page, sections, channels, offerings) exists
 * twice on the BE — under `/public-page/...` for the org's own page and
 * `/public-page/trainer/...` for the caller's own (trainer) page —
 * `PublicPageOwnerArgs.ownerType` selects the branch; both always carry
 * `organizationId` (the route is always org-scoped).
 *
 * `state` is a third, deliberately minimal route (`fetchOrgPublicPageState`)
 * with no owner branch and no `public_page_manage` requirement — see
 * `UiPublicPageState` doc.
 *
 * `channels`/`offerings` PATCH is FULL-REPLACE on the BE (same `.strict()`
 * Upsert*Dto as POST) — `updateContactChannel`/`updateServiceOffering` take
 * the whole object, never a partial.
 */
import type {
  ApiUpsertContactChannelDto,
  ApiUpsertPublicPageDto,
  ApiUpsertPublicPageSectionDto,
  PublicPageSectionKey,
} from '@mfw/common';

import type {
  ApiContactChannel,
  ApiPublicPage,
  ApiPublicPageLocation,
  ApiPublicPageSection,
  ApiServiceOffering,
  ApiSocialLink,
  PublicPageCurrency,
  UiContactChannel,
  UiPublicPage,
  UiPublicPageLocation,
  UiPublicPageSection,
  UiPublicPageState,
  UiServiceOffering,
  UiSocialLink,
  UiUpsertPublicPageLocation,
  UiUpsertServiceOffering,
  UiUpsertSocialLink,
} from '../schemas/publicPage.schema';
import type { BaseApiResponse } from './types/api.types';

import {
  mapContactChannelFromApi,
  mapPublicPageFromApi,
  mapPublicPageLocationFromApi,
  mapPublicPageSectionFromApi,
  mapServiceOfferingFromApi,
  mapSocialLinkFromApi,
  mapUpsertPublicPageLocationToApi,
  mapUpsertServiceOfferingToApi,
  mapUpsertSocialLinkToApi,
} from './mappers/publicPage.mappers';
import { destroy, get, patch, post, put } from './axios';

export type PublicPageOwnerType = 'organization' | 'trainer';

export type PublicPageOwnerArgs = {
  organizationId: string;
  ownerType: PublicPageOwnerType;
};

function publicPageBasePath({
  organizationId,
  ownerType,
}: PublicPageOwnerArgs) {
  const base = `/v2/organizations/${organizationId}/public-page`;
  return ownerType === 'trainer' ? `${base}/trainer` : base;
}

// ── page ────────────────────────────────────────────────────────────────

// The three reads below back React Query queries: "nothing there" is `null`,
// never `undefined` — React Query 5 fails a query that resolves `undefined`
// ("… data is undefined"). A platform account reading an organization that
// has no page yet gets `data: null` from the BE (a non-provisioning read).
export async function fetchPublicPage(args: PublicPageOwnerArgs) {
  const response = await get<BaseApiResponse<ApiPublicPage>>(
    publicPageBasePath(args),
  );
  return response?.data ? mapPublicPageFromApi(response.data) : null;
}

export type UpdatePublicPageArgs = PublicPageOwnerArgs & {
  data: ApiUpsertPublicPageDto;
};

export async function updatePublicPage({
  data,
  ...owner
}: UpdatePublicPageArgs): Promise<UiPublicPage | undefined> {
  const response = await put<ApiPublicPage>(publicPageBasePath(owner), data);
  return response?.data ? mapPublicPageFromApi(response.data) : undefined;
}

// ── state (F4 gate) ────────────────────────────────────────────────────

export type FetchOrgPublicPageStateArgs = { organizationId: string };

export async function fetchOrgPublicPageState({
  organizationId,
}: FetchOrgPublicPageStateArgs): Promise<UiPublicPageState | null> {
  const response = await get<BaseApiResponse<UiPublicPageState>>(
    `/v2/organizations/${organizationId}/public-page/state`,
  );
  return response?.data ?? null;
}

// ── sections ───────────────────────────────────────────────────────────

export type UpsertPublicPageSectionArgs = PublicPageOwnerArgs & {
  key: PublicPageSectionKey;
  data: Omit<ApiUpsertPublicPageSectionDto, 'key'>;
};

// `key` is taken once and threaded into both the URL and the body — the BE
// 400s if they disagree, so this signature makes that mismatch unrepresentable
// rather than asking every caller to repeat the key in `data` too.
export async function upsertPublicPageSection({
  key,
  data,
  ...owner
}: UpsertPublicPageSectionArgs): Promise<UiPublicPageSection | undefined> {
  const response = await put<ApiPublicPageSection>(
    `${publicPageBasePath(owner)}/sections/${key}`,
    { key, ...data },
  );
  return response?.data
    ? mapPublicPageSectionFromApi(response.data)
    : undefined;
}

// ── contact channels ──────────────────────────────────────────────────

export async function fetchContactChannels(
  args: PublicPageOwnerArgs,
): Promise<UiContactChannel[]> {
  const response = await get<BaseApiResponse<ApiContactChannel[]>>(
    `${publicPageBasePath(args)}/channels`,
  );
  return response?.data?.map(mapContactChannelFromApi) ?? [];
}

export type CreateContactChannelArgs = PublicPageOwnerArgs & {
  data: ApiUpsertContactChannelDto;
};

export async function createContactChannel({
  data,
  ...owner
}: CreateContactChannelArgs): Promise<UiContactChannel | undefined> {
  const response = await post<ApiContactChannel>(
    `${publicPageBasePath(owner)}/channels`,
    data,
  );
  return response?.data ? mapContactChannelFromApi(response.data) : undefined;
}

export type UpdateContactChannelArgs = PublicPageOwnerArgs & {
  id: string;
  data: ApiUpsertContactChannelDto;
};

// Full-replace (see file doc) — `data` is the whole channel, never a partial.
export async function updateContactChannel({
  id,
  data,
  ...owner
}: UpdateContactChannelArgs): Promise<UiContactChannel | undefined> {
  const response = await patch<ApiContactChannel>(
    `${publicPageBasePath(owner)}/channels/${id}`,
    data,
  );
  return response?.data ? mapContactChannelFromApi(response.data) : undefined;
}

export type DeleteContactChannelArgs = PublicPageOwnerArgs & { id: string };

export async function deleteContactChannel({
  id,
  ...owner
}: DeleteContactChannelArgs): Promise<void> {
  await destroy(`${publicPageBasePath(owner)}/channels/${id}`);
}

// ── service offerings ─────────────────────────────────────────────────

export async function fetchServiceOfferings(
  args: PublicPageOwnerArgs,
): Promise<UiServiceOffering[]> {
  const response = await get<BaseApiResponse<ApiServiceOffering[]>>(
    `${publicPageBasePath(args)}/offerings`,
  );
  return response?.data?.map(mapServiceOfferingFromApi) ?? [];
}

export type CreateServiceOfferingArgs = PublicPageOwnerArgs & {
  data: UiUpsertServiceOffering;
};

export async function createServiceOffering({
  data,
  ...owner
}: CreateServiceOfferingArgs): Promise<UiServiceOffering | undefined> {
  const response = await post<ApiServiceOffering>(
    `${publicPageBasePath(owner)}/offerings`,
    mapUpsertServiceOfferingToApi(data),
  );
  return response?.data ? mapServiceOfferingFromApi(response.data) : undefined;
}

export type UpdateServiceOfferingArgs = PublicPageOwnerArgs & {
  id: string;
  data: UiUpsertServiceOffering;
};

// Full-replace (see file doc) — `data` is the whole offering, never a partial.
export async function updateServiceOffering({
  id,
  data,
  ...owner
}: UpdateServiceOfferingArgs): Promise<UiServiceOffering | undefined> {
  const response = await patch<ApiServiceOffering>(
    `${publicPageBasePath(owner)}/offerings/${id}`,
    mapUpsertServiceOfferingToApi(data),
  );
  return response?.data ? mapServiceOfferingFromApi(response.data) : undefined;
}

export type DeleteServiceOfferingArgs = PublicPageOwnerArgs & { id: string };

export async function deleteServiceOffering({
  id,
  ...owner
}: DeleteServiceOfferingArgs): Promise<void> {
  await destroy(`${publicPageBasePath(owner)}/offerings/${id}`);
}

// ── locations (Task 15 — closes the F2 gap for the `locations` section) ──

export async function fetchPublicPageLocations(
  args: PublicPageOwnerArgs,
): Promise<UiPublicPageLocation[]> {
  const response = await get<BaseApiResponse<ApiPublicPageLocation[]>>(
    `${publicPageBasePath(args)}/locations`,
  );
  return response?.data?.map(mapPublicPageLocationFromApi) ?? [];
}

export type CreatePublicPageLocationArgs = PublicPageOwnerArgs & {
  data: UiUpsertPublicPageLocation;
};

export async function createPublicPageLocation({
  data,
  ...owner
}: CreatePublicPageLocationArgs): Promise<UiPublicPageLocation | undefined> {
  const response = await post<ApiPublicPageLocation>(
    `${publicPageBasePath(owner)}/locations`,
    mapUpsertPublicPageLocationToApi(data),
  );
  return response?.data
    ? mapPublicPageLocationFromApi(response.data)
    : undefined;
}

export type UpdatePublicPageLocationArgs = PublicPageOwnerArgs & {
  id: string;
  data: UiUpsertPublicPageLocation;
};

// Full-replace (same convention as channels/offerings) — `data` is the whole
// location, never a partial.
export async function updatePublicPageLocation({
  id,
  data,
  ...owner
}: UpdatePublicPageLocationArgs): Promise<UiPublicPageLocation | undefined> {
  const response = await patch<ApiPublicPageLocation>(
    `${publicPageBasePath(owner)}/locations/${id}`,
    mapUpsertPublicPageLocationToApi(data),
  );
  return response?.data
    ? mapPublicPageLocationFromApi(response.data)
    : undefined;
}

export type DeletePublicPageLocationArgs = PublicPageOwnerArgs & {
  id: string;
};

export async function deletePublicPageLocation({
  id,
  ...owner
}: DeletePublicPageLocationArgs): Promise<void> {
  await destroy(`${publicPageBasePath(owner)}/locations/${id}`);
}

// ── social links (fix round 1/5, Finding 2) ──────────────────────────────

export async function fetchSocialLinks(
  args: PublicPageOwnerArgs,
): Promise<UiSocialLink[]> {
  const response = await get<BaseApiResponse<ApiSocialLink[]>>(
    `${publicPageBasePath(args)}/social`,
  );
  return response?.data?.map(mapSocialLinkFromApi) ?? [];
}

export type CreateSocialLinkArgs = PublicPageOwnerArgs & {
  data: UiUpsertSocialLink;
};

export async function createSocialLink({
  data,
  ...owner
}: CreateSocialLinkArgs): Promise<UiSocialLink | undefined> {
  const response = await post<ApiSocialLink>(
    `${publicPageBasePath(owner)}/social`,
    mapUpsertSocialLinkToApi(data),
  );
  return response?.data ? mapSocialLinkFromApi(response.data) : undefined;
}

export type UpdateSocialLinkArgs = PublicPageOwnerArgs & {
  id: string;
  data: UiUpsertSocialLink;
};

// Full-replace (same convention as channels/offerings/locations).
export async function updateSocialLink({
  id,
  data,
  ...owner
}: UpdateSocialLinkArgs): Promise<UiSocialLink | undefined> {
  const response = await patch<ApiSocialLink>(
    `${publicPageBasePath(owner)}/social/${id}`,
    mapUpsertSocialLinkToApi(data),
  );
  return response?.data ? mapSocialLinkFromApi(response.data) : undefined;
}

export type DeleteSocialLinkArgs = PublicPageOwnerArgs & { id: string };

export async function deleteSocialLink({
  id,
  ...owner
}: DeleteSocialLinkArgs): Promise<void> {
  await destroy(`${publicPageBasePath(owner)}/social/${id}`);
}

// ── currency suggestion (O-7 — flagged by Task 9, closed here) ──────────

export async function fetchPublicPageCurrencySuggestion(
  args: PublicPageOwnerArgs,
): Promise<PublicPageCurrency | null> {
  const response = await get<BaseApiResponse<PublicPageCurrency>>(
    `${publicPageBasePath(args)}/currency-suggestion`,
  );
  return response?.data ?? null;
}
